import type { OccurrenceDto, PersonDto } from "../api/client";
import { datePart, formatShortDate, formatTimeRange, formatWeekday } from "../format";
import { timetableClasses } from "../timetablePresentation";
import { isImportedSource } from "../importedSources";

export function PersonChips({ ids, persons }: { ids: string[]; persons: PersonDto[] }) {
  if (ids.length === 0) return null;
  return (
    <span className="chips">
      {ids.map((id) => {
        const p = persons.find((x) => x.id === id);
        return (
          <span key={id} className="chip" style={{ borderColor: p?.color }}>
            <span className="swatch" style={{ background: p?.color }} aria-hidden="true" />
            {p?.displayName ?? "?"}
          </span>
        );
      })}
    </span>
  );
}

/** Termine nach Tag gruppiert. Farben unterstützen, sind aber nie die einzige Kennzeichnung (Namen stehen dabei). */
export function EventList({
  occurrences,
  persons,
  emptyText,
  onSelect,
  calendarColors,
}: {
  occurrences: OccurrenceDto[];
  persons: PersonDto[];
  emptyText: string;
  onSelect?: (o: OccurrenceDto) => void;
  calendarColors?: Record<string, string>;
}) {
  if (occurrences.length === 0) return <p className="muted">{emptyText}</p>;
  const days = new Map<string, OccurrenceDto[]>();
  for (const o of occurrences) {
    const d = datePart(o.start);
    days.set(d, [...(days.get(d) ?? []), o]);
  }
  return (
    <div className="event-days">
      {[...days.entries()].map(([day, items]) => (
        <section key={day} className="event-day">
          <h3>
            {formatWeekday(day)} <span className="muted">{formatShortDate(day).split(" ")[1]}</span>
          </h3>
          <ul className="events">
            {items.map((o) => (
              <li key={o.occurrenceId} className={timetableClasses(o.source, o.title).trim() || undefined} data-source={o.source}
                style={{ borderLeftColor: `var(--event-accent, ${calendarColors?.[o.calendarId] ?? "var(--primary)"})`, background: "var(--event-bg, var(--surface))" }}>
                <button type="button" className="event" onClick={() => onSelect?.(o)} disabled={!onSelect}>
                  <span className="time">{formatTimeRange(o.start, o.end, o.allDay)}</span>
                  <span className="title">
                    {o.title}
                    {o.isRecurring && <span className="tag" title="Serientermin"> ↻</span>}
                  </span>
                  {o.location && <span className="muted small">📍 {o.location}</span>}
                  <PersonChips ids={o.participants.map((p) => p.personId)} persons={persons} />
                  {!isImportedSource(o.source) && o.createdByPersonId && !o.participants.some((p) => p.personId === o.createdByPersonId) && (
                    <span className="muted small">eingetragen von {persons.find((p) => p.id === o.createdByPersonId)?.displayName ?? "?"}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
